import React, { useState, useEffect, useMemo } from "react";
import { accountAPI, api } from "services";
import { ProfilePhoto, MyPhoneInput } from "components";
import Creatable from "react-select/creatable";
import { creatableStyle } from "assets/themes";
import { toast } from "sonner";
import useUniversalStore from "store/universalStore";
import useOffcanvasStore from "store/offcanvasStore";
import { useForm, Controller, useWatch } from "react-hook-form";
import { defaultValues, validateAccountName } from "./config";
import { getAllTagsFromAccounts, validatePhone } from "utils";

const nameRules = {
  required: "Name is required",
  maxLength: { value: 50, message: "Name cannot exceed 50 characters" },
  validate: validateAccountName
};

const phoneRules = (isShared) => ({
  validate: (value) => {
    if (!value || value === "+91") {
      return isShared ? "Phone is required for shared accounts" : true;
    }
    return validatePhone(value);
  }
});

const tagRules = {
  maxLength: { value: 20, message: "Tag cannot exceed 20 characters" }
};

const NewAccount = () => {
  const { write, accounts, userBasic, user } = useUniversalStore((state) => state);
  const { closeOffcanvas } = useOffcanvasStore();
  const { control, handleSubmit, setValue, trigger, register, formState: { errors, isSubmitting, isValid } } = useForm({
    defaultValues,
    mode: "onBlur"
  });

  const photo = useWatch({ control, name: "photo" });
  const phone = useWatch({ control, name: "phone" });
  const isShared = useWatch({ control, name: "isShared" });

  const tags = getAllTagsFromAccounts(accounts);

  const mappedTags = tags.map((tag) => ({ value: tag, label: tag?.toUpperCase() }));

  const [contacts, setContacts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    trigger("phone");
  }, [isShared]);

  useEffect(() => {
    const fetchContacts = async () => {
      setIsFetching(true);
      try {
        const res = await api.getContacts();
        if (res?.[0]) {
          const phonebook = res[0].get("phonebook") || [];
          setContacts(phonebook);
        }
      } catch (error) {
        console.error("Failed to fetch contacts", error);
      } finally {
        setIsFetching(false);
      }
    };
    fetchContacts();
  }, []);

  const contactOptions = useMemo(() => {
    return contacts
      .filter((c) => c?.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({
        value: c?.number?.replace(/\s+/g, ""),
        label: c?.name
      }));
  }, [contacts]);

  const handleContactSelect = (selectedOption) => {
    if (!selectedOption) {
      setValue("name", "", { shouldValidate: true });
      setValue("phone", "");
      return;
    }
    setValue("name", selectedOption.label, { shouldValidate: true });
    if (!selectedOption.__isNew__) {
      setValue("phone", selectedOption.value || "");
    }
  };

  const onSubmit = async (data) => {
    try {
      const result = await accountAPI.createAccount(
        data.name,
        data.phone,
        data.address,
        data.details,
        data.tag?.value ?? null,
        data.photo.url,
        data.isShared,
        userBasic?.business?.name || user?.get("username"),
        data.checkboxSMS,
      );

      write({
        accounts: await accountAPI.getAccounts(),
        accountId: result.id
      });

      toast.success("Account created successfully");
      closeOffcanvas();
    } catch (error) {
      toast.error("Failed to create account");
    }
  };

  const inputClass = (name) => `form-control border-0 ${errors[name] ? "is-invalid" : ""}`;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>

      <div className="d-flex flex-wrap align-items-center">
        <div className="w-50">
          <ProfilePhoto
            key="add-profile-photo"
            url={photo?.url}
            loading={photo?.loading}
            updateField={(field, data) => setValue(field, data)}
            disabled={isSubmitting}
          />
        </div>
        <div className="w-50 d-flex flex-column flex-wrap flex-grow-1 text-end">
            <div className="p-0">
              <Controller
                name="name"
                control={control}
                rules={nameRules}
                render={({ field: { value } }) => (
                  <Creatable
                    options={contactOptions}
                    value={value ? { label: value, value: phone } : null}
                    classNamePrefix="rs"
                    isLoading={isFetching}
                    isClearable
                    placeholder="Name"
                    onChange={handleContactSelect}
                    classNames={creatableStyle}
                    className={errors.name ? "is-invalid" : value ? "is-valid" : ""}
                    isDisabled={isSubmitting}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: "#d83047",
                        primary25: "#f47788",
                        primary50: "#d83047"
                      }
                    })}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderTop: "none",
                        borderLeft: "none",
                        borderRight: "none",
                        borderRadius: 0,
                        borderBottomWidth: 1,
                        borderBottomColor: "#d83047",
                        boxShadow: "none"
                      })
                    }}
                  />
                )}
              />
              {errors.name && (<div className="invalid-feedback d-block">{errors.name.message}</div>)}
            </div>
            <div className="d-flex justify-content-end align-items-center mt-2 text-end">
              <label className="form-check-label mb-0 me-2" htmlFor="checkboxSMS" style={{ marginBottom: 0 }}>
                <small>Notify via SMS</small>
              </label>
              <div className="form-check form-switch cursor-pointer">
                <input
                  className="form-check-input cursor-pointer"
                  type="checkbox"
                  id="checkboxSMS"
                  {...register("checkboxSMS")}
                  disabled={isSubmitting}
                  style={{ verticalAlign: 'middle' }}
                />
              </div>
            </div>
        </div>
      </div>

      <hr />
      <div className="d-flex flex-wrap align-items-center">
        <span className="w-25">Phone</span>
        <div className="phone-input-wrapper flex-grow-1 d-flex flex-column align-items-end">
          <Controller
            name="phone"
            control={control}
            rules={phoneRules(isShared)}
            render={({ field }) => (
              <MyPhoneInput
                className="text-end"
                field={field}
                error={errors.phone}
                isSubmitting={isSubmitting}
              />
            )}
          />
          {errors.phone && <div className="invalid-feedback d-block text-end">{errors.phone.message}</div>}
        </div>
      </div>

      <hr />
      <div className="d-flex flex-wrap align-items-center">
        <span className="w-50">Tag</span>
        <div className="flex-grow-1">
          <Controller
            name="tag"
            control={control}
            rules={tagRules}
            render={({ field: { value, onChange } }) => (
              <Creatable
                isClearable
                options={mappedTags}
                value={value}
                onChange={onChange}
                placeholder="Select or create"
                classNamePrefix="rs"
                classNames={creatableStyle}
                className={`text-end ${errors.tag ? "is-invalid" : value ? "is-valid" : ""}`}
                isDisabled={isSubmitting}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "#d83047",
                    primary25: "#f47788",
                    primary50: "#d83047"
                  }
                })}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: "none",
                    boxShadow: "none"
                  })
                }}
              />
            )}
          />
          {errors.tag && <div className="invalid-feedback d-block">{errors.tag.message}</div>}
        </div>
      </div>

      <hr />
      <div className="d-flex flex-wrap align-items-center">
        <span className="w-25">Address</span>
        <div className="flex-grow-1">
          <div className="form-control border-0 p-0">
            <input
              {...register("address")}
              type="text"
              className={`text-end ${inputClass("address")} `}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      <hr />
      <div className="d-flex flex-wrap align-items-center">
        <span className="w-25">Other details</span>
        <div className="flex-grow-1">
          <div className="form-control border-0 p-0">
            <textarea
              {...register("details")}
              className={`text-end ${inputClass("details")} `}
              rows={2}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      <hr />

      <div className="px-3 position-absolute bottom-0 start-0 end-0 mb-3">
        <button
          type="submit"
          className="btn btn-app w-100 py-2 py-md-3 fs-5 fw-bold text-uppercase"
          disabled={isSubmitting || photo?.loading || !isValid}
        >
          {photo?.loading ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : isSubmitting ? "Adding..." : "Add"}
        </button>
      </div>
    </form>
  );
};

export default NewAccount;
